// Lucian UX/UI Prompt #098 · MIT License (c) 2026 Seokwoooo · https://lucianlabs.dev/uxui-prompt/#098
// 主要な数字。大きな数字は変わった桁だけ回り、下の細い線は日ごとの推移。下の札で指標を切り替える。
// 推移の線はなぞる・左右キーでその日の値が読める（値は下の札と上の数字でも読めるので、なぞらなくても困らない）。
import React, { useEffect, useMemo, useRef, useState } from 'react';
import RollingNumber from './RollingNumber';
import { sparkPoints } from '../../utils/home-viz.mjs';
import './lucian.css';

const H = 56, PAD = 4;
const md = (ymd) => { const [, m, d] = String(ymd).split('-').map(Number); return `${m}/${d}`; };

export default function MetricHero({ items, selected, onSelect, daily, periodLabel }) {
  const cur = items.find(i => i.key === selected) || items[0];
  const series = useMemo(() => (daily || []).map(d => ({ date: d.date, v: Number(d[cur.key]) || 0 })), [daily, cur.key]);
  // 実際の幅で描く（viewBox を引き伸ばすと点が楕円に崩れるため）
  const svgBox = useRef(null);
  const [W, setW] = useState(300);
  useEffect(() => {
    const el = svgBox.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(([e]) => setW(Math.max(120, Math.round(e.contentRect.width))));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const pts = useMemo(() => sparkPoints(series.map(s => s.v), W, H, PAD), [series, W]);
  const [hover, setHover] = useState(null);

  const pathD = pts.map((p, i) => `${i ? 'L' : 'M'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
  const areaD = pts.length ? `${pathD} L${pts[pts.length - 1].x.toFixed(1)},${H} L${pts[0].x.toFixed(1)},${H} Z` : '';
  const at = hover != null ? pts[hover] : null;

  const pick = (clientX, svg) => {
    const r = svg.getBoundingClientRect();
    const x = ((clientX - r.left) / r.width) * W;
    let best = 0;
    pts.forEach((p, i) => { if (Math.abs(p.x - x) < Math.abs(pts[best].x - x)) best = i; });
    setHover(best);
  };
  const onKey = (e) => {
    if (!pts.length || (e.key !== 'ArrowLeft' && e.key !== 'ArrowRight')) return;
    e.preventDefault();
    const base = hover == null ? pts.length - 1 : hover;
    setHover(Math.max(0, Math.min(pts.length - 1, base + (e.key === 'ArrowRight' ? 1 : -1))));
  };

  return (
    <div>
      <div className="lx-hero__label">{cur.label}</div>
      <RollingNumber value={cur.value} ariaLabel={`${cur.label} ${cur.value}`} />
      <div className="lx-hero__sub" aria-live="polite">
        {at ? <><b>{md(series[hover].date)}</b> の{cur.label} <b>{series[hover].v}</b></> : periodLabel}
      </div>
      <div ref={svgBox}>
      {pts.length > 0 && (
        <svg className="lx-spark" viewBox={`0 0 ${W} ${H}`} tabIndex={0}
          role="img" aria-label={`${cur.label}の日ごとの推移（${series.length}日分）。左右キーで日を選べます`}
          onPointerMove={e => pick(e.clientX, e.currentTarget)} onPointerDown={e => pick(e.clientX, e.currentTarget)}
          onPointerLeave={() => setHover(null)} onBlur={() => setHover(null)} onKeyDown={onKey}>
          <path className="lx-spark__area" d={areaD} />
          <path className="lx-spark__line" d={pathD} />
          {at && <line className="lx-spark__cross" x1={at.x} x2={at.x} y1={0} y2={H} />}
          {(() => { const p = at || pts[pts.length - 1]; return <circle className="lx-spark__dot" cx={p.x} cy={p.y} r={4} />; })()}
        </svg>
      )}
      </div>
      <div className="lx-tabs" role="tablist" aria-label="指標">
        {items.map(i => (
          <button key={i.key} type="button" role="tab" aria-selected={i.key === cur.key} className="lx-tab"
            onClick={() => { setHover(null); onSelect(i.key); }}>
            <small>{i.label}</small><b>{i.value}</b>
          </button>
        ))}
      </div>
    </div>
  );
}
